/**
 * Main form page with all sections
 */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-console */

import { useLocation } from 'preact-iso';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useAuth } from '@ox/visits-ui/context/AuthContext';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { NavigationButtons } from '@ox/visits-ui/components/shared/NavigationButtons';
import { ProgressBar } from '@ox/visits-ui/components/shared/ProgressBar';
import { SaveIndicator } from '@ox/visits-ui/components/shared/SaveIndicator';
import { ResumeDialog } from '@ox/visits-ui/components/shared/ResumeDialog';
import { SuccessPage } from '@ox/visits-ui/components/pages/SuccessPage';
import { EscalationBanner } from '@ox/visits-ui/components/shared/EscalationBanner';
import { sectionComponents } from '@ox/visits-ui/components/shared/Sections';
import { ErrorNotification, Progress } from '@ox/blox';

export function FormPage() {
  const { isAuthenticated, userData } = useAuth();
  const location = useLocation();
  const {
    sections,
    draft,
    formData,
    currentSection,
    isLoading,
    viewMode,
    escalations,
    error,
    showEscalationBanner,
    isMediaUploading,
    mediaUploadProgress,
    goToSection,
    completeSection,
    submitForm,
    createDraft,
    dismissEscalationBanner,
    setError,
  } = useForm();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const totalSections = sections.length > 0 ? sections.length : 0;
  const currentSectionKey = sections[currentSection];
  const CurrentSection = typeof currentSectionKey === 'number' ? sectionComponents[currentSectionKey] : undefined;
  const canRenderForm = (Boolean(draft?.id) || isInitialized) && sections.length > 0;

  const handleNext = async () => {
    if (!viewMode) {
      const sectionIndex = sections[currentSection];
      if (typeof sectionIndex !== 'number') {
        return;
      }
      const success = await completeSection(sectionIndex);
      if (!success) {
        return;
      }
    }
    const nextSection = currentSection + 1;
    if (nextSection < sections.length) {
      goToSection(nextSection);
    }
  };

  const handleResume = () => {
    setShowResumeDialog(false);
  };

  const handleStartFresh = async () => {
    await createDraft(userData.property_address, true);
    setShowResumeDialog(false);
  };

  const handlePrevious = () => {
    const previousSection = currentSection > 0 ? currentSection - 1 : 0;
    goToSection(previousSection);
  };

  const handleSubmit = async () => {
    try {
      const finalSection = sections[sections.length - 1];
      if (typeof finalSection !== 'number') {
        return;
      }
      const success = await submitForm(finalSection);
      if (success) {
        goToSection(totalSections);
      }
    } catch (error) {
      console.error('Submit failed:', error);
    }
  };

  const onErrorNotificationDismiss = useCallback(() => {
    setError(null);
  }, [setError]);

  // Upload modal is intentionally non-dismissable.

  useEffect(() => {
    if (!isAuthenticated) {
      location.route('/');
    }
  }, [isAuthenticated, location]);

  // Auto-create draft with property address from token
  useEffect(() => {
    const initializeDraft = async () => {
      if (isInitialized || isLoading || draft?.id || !userData?.property_address) {
        return;
      }
      try {
        const draft = await createDraft(userData.property_address, false);
        if (draft.currentSection > 0) {
          setShowResumeDialog(true);
        }
      } finally {
        setIsInitialized(true);
      }
    };
    initializeDraft();
  }, [createDraft, isInitialized, draft?.id, isLoading, userData?.property_address]);

  if (isLoading || (!draft?.id && !isInitialized)) {
    return (
      <div className="container">
        <div className="loading">
          <div>
            {userData?.property_address
              ? `Initializing form for ${userData.property_address}...`
              : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  if (!canRenderForm) {
    return (
      <div className="container">
        <div className="loading">Preparing your form...</div>
      </div>
    );
  }

  if (showResumeDialog && draft && !draft.submittedAt) {
    return (
      <ResumeDialog
        timestamp={new Date(draft.updatedAt)}
        sectionsCompleted={draft.sectionsCompleted}
        totalSections={sections.length - 1}
        onResume={handleResume}
        onStartFresh={handleStartFresh}
      />
    );
  }

  const canAccess = formData.were_able_to_access_property === 'Yes';

  return <>
    {isMediaUploading && (
      <div
        aria-label="Uploading media"
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0, 0, 0, 0.45)',
          padding: 24,
        }}
      >
        <div
          style={{
            width: 'min(520px, 100%)',
            background: 'var(--surface, #fff)',
            color: 'var(--text, #111)',
            borderRadius: 12,
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Progress
              value={mediaUploadProgress}
              max={100}
              type="circular"
              label="Uploading…"
              labelPosition="after"
            />
          </div>
        </div>
      </div>
    )}

    {showEscalationBanner && escalations.length > 0 && (
      <EscalationBanner
        escalations={escalations}
        onDismiss={dismissEscalationBanner}
      />
    )}

    {draft?.submittedAt && (currentSection >= totalSections || (!canAccess && currentSection === 3))
      ? <SuccessPage />
      : (
        <div className="container">
          <SaveIndicator />
          <ErrorNotification open={!!error} onDismiss={onErrorNotificationDismiss}>{error}</ErrorNotification>
          <ProgressBar currentSection={currentSection + 1} totalSections={canAccess ? totalSections : 3} />
          
          {CurrentSection && <CurrentSection />}

          <NavigationButtons
            currentSection={currentSection}
            totalSections={canAccess ? totalSections : 3}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSubmit={viewMode ? undefined : handleSubmit}
            canProceed={Boolean(CurrentSection)}
          />
        </div>
      )}
  </>;
}
